// =============================================================================
// MeuDriver — Booking Flow (orchestrator)
// -----------------------------------------------------------------------------
// O fluxo de booking foi quebrado em 3 sub-componentes — esta página apenas
// orquestra o estado de step, faz o fetch do instrutor e dispara o checkout.
// Veja `./booking/` para os steps.
// =============================================================================

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { format } from 'date-fns'
import { toast } from 'sonner'

import { useAuth } from '@/hooks/useAuth'
import { useBookingStore } from '@/store/bookingStore'
import { supabase } from '@/lib/supabase'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { track } from '@/lib/analytics'
import {
  getPlatformFee,
  getInstructorPayout,
} from '@/lib/utils'
import {
  PACKAGE_OPTIONS,
  TRIAL_DURATION_MINUTES,
  TRIAL_PRICE_MULTIPLIER,
} from '@/lib/constants'
import type { InstructorWithProfile } from '@/types/database'

import {
  StepIndicator,
  RedirectingToPayment,
  type Step,
  type AppliedCoupon,
  type AppliedGiftCard,
} from './booking/BookingFlowShared'
import { BookingStep1DateTime } from './booking/BookingStep1DateTime'
import { BookingStep2Address } from './booking/BookingStep2Address'
import { BookingStep3Confirm } from './booking/BookingStep3Confirm'

function buildScheduledAt(dateStr: string, timeStr: string): string {
  return new Date(`${dateStr}T${timeStr}:00`).toISOString()
}

export default function BookingFlow() {
  const { instructorId } = useParams<{ instructorId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const store = useBookingStore()

  const [step, setStep] = useState<Step>(1)
  const [instructor, setInstructor] = useState<InstructorWithProfile | null>(null)
  const [loadingInstructor, setLoadingInstructor] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(null)
  const [appliedGiftCard, setAppliedGiftCard] = useState<AppliedGiftCard | null>(null)
  const [useReferralCredit, setUseReferralCredit] = useState(false)
  const [availableReferralCredits, setAvailableReferralCredits] = useState(0)

  // Carrega saldo de créditos de indicação do aluno
  useEffect(() => {
    if (!user?.id) return
    supabase
      .from('profiles')
      .select('referral_credits')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setAvailableReferralCredits(Number((data as { referral_credits?: number }).referral_credits ?? 0))
      })
  }, [user?.id])

  const progressValue = step === 1 ? 33 : step === 2 ? 66 : 100

  // Initialize store with instructor id; reset on unmount.
  useEffect(() => {
    if (instructorId) store.setInstructor(instructorId)
    return () => { store.reset() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructorId])

  // Fetch instructor.
  useEffect(() => {
    if (!instructorId) return
    setLoadingInstructor(true)

    supabase
      .from('instructors')
      .select('*, profile:profiles(*)')
      .eq('id', instructorId)
      .eq('status', 'approved')
      .single()
      .then(({ data, error }) => {
        if (error || !data) {
          toast.error('Instrutor não encontrado.')
          navigate('/instrutores')
          return
        }
        setInstructor(data as unknown as InstructorWithProfile)
      })
      .finally(() => setLoadingInstructor(false))
  }, [instructorId, navigate])

  async function handleConfirm() {
    if (!user || !instructor || !store.selectedDate || !store.selectedTime) return

    setIsSubmitting(true)
    try {
      const scheduledAt = buildScheduledAt(
        format(store.selectedDate, 'yyyy-MM-dd'),
        store.selectedTime,
      )

      const hourlyRate = instructor.hourly_rate ?? 0
      const isTrial   = store.bookingMode === 'trial'
      const isPackage = store.bookingMode === 'package'
      const selectedPkg = PACKAGE_OPTIONS.find((p) => p.quantity === store.packageQuantity) ?? PACKAGE_OPTIONS[1]
      const effectiveDuration = isTrial ? TRIAL_DURATION_MINUTES : store.durationMinutes
      const unitPrice = hourlyRate * (effectiveDuration / 60) * (isTrial ? TRIAL_PRICE_MULTIPLIER : 1)
      const subtotal  = isPackage
        ? unitPrice * selectedPkg.quantity * (1 - selectedPkg.discountPercent / 100)
        : unitPrice
      const platformFee = getPlatformFee(subtotal)
      const instructorPayout = getInstructorPayout(subtotal)

      const discount = appliedCoupon
        ? appliedCoupon.discount_type === 'percent'
          ? (subtotal * appliedCoupon.discount_value) / 100
          : Math.min(appliedCoupon.discount_value, subtotal)
        : 0

      const total = subtotal + platformFee - discount

      // 1. Create the booking row (status = pending_payment).
      //    Os valores financeiros aqui são PREVISÃO — o backend recalcula
      //    via `apply_checkout_pricing` antes de criar a preferência MP.
      const { data: bookingData, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          student_id: user.id,
          instructor_id: instructor.id,
          scheduled_at: scheduledAt,
          duration_minutes: effectiveDuration,
          booking_type: store.bookingMode,
          lesson_count: isPackage ? selectedPkg.quantity : 1,
          pickup_address: store.pickupAddress || null,
          pickup_lat: store.pickupLat,
          pickup_lng: store.pickupLng,
          notes: store.notes || null,
          total_price: total,
          platform_fee: platformFee,
          instructor_payout: instructorPayout,
          coupon_id: appliedCoupon?.id ?? null,
          discount_amount: discount > 0 ? discount : null,
          status: 'pending_payment',
        })
        .select('id')
        .single()

      if (bookingError) throw new Error(bookingError.message)
      const bookingId = bookingData.id

      track('booking_created', {
        booking_type: store.bookingMode,
        instructor_id: instructor.id,
        lesson_count: isPackage ? selectedPkg.quantity : 1,
        has_coupon: Boolean(appliedCoupon),
      })

      // 2. Call Edge Function to get Mercado Pago checkout URL.
      setRedirecting(true)
      track('checkout_started', { booking_id: bookingId })

      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'create-checkout',
        {
          body: {
            booking_id: bookingId,
            coupon_code: appliedCoupon?.code ?? null,
            gift_card_code: appliedGiftCard?.code ?? null,
            use_referral_credit: useReferralCredit,
          },
        },
      )

      if (fnError) throw new Error(fnError.message)

      // Caso zero_total: o backend já confirmou (gift card + crédito cobriram tudo)
      if (fnData?.zero_total) {
        toast.success('Aula confirmada! Pagamento totalmente coberto por créditos.')
        navigate(`/aluno/aulas/${bookingId}`)
        return
      }

      if (!fnData?.checkout_url) throw new Error('URL de pagamento não recebida.')

      window.location.href = fnData.checkout_url
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao criar reserva.'
      toast.error(msg)
      setIsSubmitting(false)
      setRedirecting(false)
    }
  }

  if (loadingInstructor) {
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    )
  }

  if (!instructor) return null

  if (redirecting) {
    return (
      <div className="max-w-lg mx-auto">
        <RedirectingToPayment />
      </div>
    )
  }

  const stepTitle = step === 1
    ? 'Escolha a data e horário'
    : step === 2
      ? 'Local de partida'
      : 'Confirmar e pagar'

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(`/instrutores/${instructor.slug ?? instructorId}`)}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft className="h-4 w-4" />
          Voltar ao perfil do instrutor
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Agendar aula</h1>
      </div>

      <Progress value={progressValue} className="h-2" />
      <StepIndicator currentStep={step} />
      <h2 className="text-lg font-semibold text-gray-800">{stepTitle}</h2>

      {step === 1 && (
        <BookingStep1DateTime instructor={instructor} onNext={() => setStep(2)} />
      )}
      {step === 2 && (
        <BookingStep2Address
          instructor={instructor}
          onNext={() => setStep(3)}
          onBack={() => setStep(1)}
        />
      )}
      {step === 3 && (
        <BookingStep3Confirm
          instructor={instructor}
          onBack={() => setStep(2)}
          onConfirm={handleConfirm}
          isSubmitting={isSubmitting}
          appliedCoupon={appliedCoupon}
          onApplyCoupon={setAppliedCoupon}
          appliedGiftCard={appliedGiftCard}
          onApplyGiftCard={setAppliedGiftCard}
          useReferralCredit={useReferralCredit}
          onToggleReferralCredit={setUseReferralCredit}
          availableReferralCredits={availableReferralCredits}
        />
      )}
    </div>
  )
}
